/**
 * 👤 MY PROFILE VIEW - Voir son propre profil comme les autres le voient
 * 
 * "Connais-toi toi-même" - Socrate
 * 
 * Cette vue montre à l'utilisateur :
 * - Comment son profil apparaît aux autres
 * - Ses stats (vues, likes, matchs)
 * - Un accès rapide pour modifier
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Edit, Eye, Heart, MessageCircle, Sparkles, MapPin,
  Camera, Crown, Star, TrendingUp, Users, Shield, Check,
  ChevronRight, Settings, Palette, Mail, PawPrint, Plus, Moon, X, Video
} from 'lucide-react';
import { Button } from '../../ui/button';
import { ProfileFrame } from '../shared/ProfileFrames';
import { PROFILE_HEADERS } from '../shared/ProfileHeaders';
import { SinBadge, SEVEN_SINS } from '../shadow/ShadowSins';
import { VerificationBadges, VERIFICATION_BADGES } from '../features/verification/Verification';

// 🎨 Composant de prévisualisation du profil (comme les autres le voient)
export const ProfilePreviewCard = ({ 
  profile, 
  customer,
  isDarkMode = true,
  showEditButton = true,
  onEdit,
  size = 'normal' // 'compact', 'normal', 'large'
}) => {
  if (!profile) return null;

  const sizes = {
    compact: { card: 'max-w-xs', photo: 'w-32 h-32', text: 'text-sm' },
    normal: { card: 'max-w-sm', photo: 'w-44 h-44', text: 'text-base' },
    large: { card: 'max-w-md', photo: 'w-56 h-56', text: 'text-lg' },
  };
  
  const s = sizes[size];
  
  // Photo principale - ORDRE DE PRIORITÉ :
  // 1. Photo du profil Heartbeats
  // 2. Avatar IA généré
  // 3. Photo de l'espace client (customer.profilePhoto)
  const mainPhoto = profile.photos?.[0] || null;
  const avatarImage = profile.avatarImage || null;
  const clientPhoto = customer?.profilePhoto || null;
  const displayImage = mainPhoto || avatarImage || clientPhoto;
  
  // Emoji ou initiales si pas d'image
  const avatarEmoji = profile.avatar || profile.avatarEmoji || null;
  const initials = profile.displayName?.[0] || customer?.firstName?.[0] || '?';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${s.card} mx-auto`}
    >
      <div className={`relative rounded-2xl overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700' 
          : 'bg-white border border-gray-200 shadow-xl'
      }`}>
        
        {/* Header avec photo et cadre */}
        <div className="relative">
          {/* Background gradient - PERSONNALISABLE */}
          {(() => {
            const header = PROFILE_HEADERS.find(h => h.id === profile.profileHeader) || PROFILE_HEADERS[0];
            return (
              <div 
                className="h-20" 
                style={{ background: header.style }}
              />
            );
          })()}
          
          {/* Photo de profil avec cadre - légèrement plus bas */}
          <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2">
            <ProfileFrame 
              src={displayImage}
              emoji={(!displayImage && avatarEmoji && !avatarEmoji.startsWith('data:') && !avatarEmoji.startsWith('http')) ? avatarEmoji : null}
              frameId={profile.profileFrame || 'gradient_purple'}
              size={110}
              alt={profile.displayName}
            />
            
            {/* Badges de vérification */}
            {profile.badges && profile.badges.length > 0 && (
              <div className="absolute -right-1 -bottom-1 flex gap-1">
                {profile.badges.includes('verified') && (
                  <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center border-2 border-white" title="Profil vérifié">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
                {profile.badges.includes('email') && (
                  <div className="w-7 h-7 rounded-full bg-cyan-500 flex items-center justify-center border-2 border-white" title="Email vérifié">
                    <Mail className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Bouton modifier */}
          {showEditButton && onEdit && (
            <button
              onClick={onEdit}
              className="absolute top-3 right-3 p-2 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all"
            >
              <Edit className="w-4 h-4 text-white" />
            </button>
          )}
        </div>
        
        {/* Contenu - espace réduit pour le nom sous la photo */}
        <div className="pt-12 pb-4 px-4 text-center">
          {/* Nom et âge */}
          <h3 className={`font-bold ${s.text} ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {profile.displayName || 'Anonyme'}
            {profile.age && <span className="font-normal">, {profile.age}</span>}
          </h3>
          
          {/* Ville */}
          {profile.city && (
            <div className={`flex items-center justify-center gap-1 mt-0.5 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <MapPin className="w-3 h-3" />
              <span className="text-sm">{profile.city}</span>
            </div>
          )}
          
          {/* Bio */}
          {profile.bio && (
            <p className={`mt-1.5 ${s.text} ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} line-clamp-2`}>
              &quot;{profile.bio}&quot;
            </p>
          )}
          
          {/* Passions (top 3) */}
          {profile.passions && Object.keys(profile.passions).length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-1">
              {Object.values(profile.passions).flat().slice(0, 4).map((passion, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-300"
                >
                  {passion}
                </span>
              ))}
              {Object.values(profile.passions).flat().length > 4 && (
                <span className="px-2 py-0.5 rounded-full text-xs text-gray-400">
                  +{Object.values(profile.passions).flat().length - 4}
                </span>
              )}
            </div>
          )}
          
          {/* Péché dominant (Shadow Mode) */}
          {profile.dominantSin && (
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-xs text-gray-500">Péché dominant :</span>
              <span className="text-sm">
                {SEVEN_SINS[profile.dominantSin]?.emoji} {SEVEN_SINS[profile.dominantSin]?.name}
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// 📊 Stats du profil
export const ProfileStats = ({ profile, stats, isDarkMode = true }) => {
  const statItems = [
    { icon: Eye, label: 'Vues', value: stats?.views || 0, color: 'text-blue-400' },
    { icon: Heart, label: 'Likes reçus', value: stats?.likesReceived || 0, color: 'text-pink-400' },
    { icon: Sparkles, label: 'Matchs', value: stats?.matches || 0, color: 'text-purple-400' },
    { icon: MessageCircle, label: 'Conversations', value: stats?.conversations || 0, color: 'text-green-400' },
  ];

  return (
    <div className={`grid grid-cols-4 gap-2 p-4 rounded-xl ${
      isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'
    }`}>
      {statItems.map((stat, idx) => (
        <div key={idx} className="text-center">
          <stat.icon className={`w-5 h-5 mx-auto mb-1 ${stat.color}`} />
          <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {stat.value}
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {stat.label}
          </p>
        </div>
      ))}
    </div>
  );
};

// 📸 Indicateur de source de photo avec explication
const PhotoSourceIndicator = ({ profile, customer, isDarkMode }) => {
  // Déterminer quelle source est utilisée
  const hasHeartbeatsPhoto = profile?.photos?.[0];
  const hasAvatarAI = profile?.avatarImage;
  const hasClientPhoto = customer?.profilePhoto;
  const hasEmoji = profile?.avatar || profile?.avatarEmoji;
  
  let sourceType = 'none';
  let sourceLabel = 'Aucune photo';
  let sourceIcon = '❓';
  let sourceColor = 'text-gray-400';
  
  if (hasHeartbeatsPhoto) {
    sourceType = 'heartbeats';
    sourceLabel = 'Photo Heartbeats';
    sourceIcon = '📸';
    sourceColor = 'text-pink-400';
  } else if (hasAvatarAI) {
    sourceType = 'avatar';
    sourceLabel = 'Avatar IA';
    sourceIcon = '✨';
    sourceColor = 'text-purple-400';
  } else if (hasClientPhoto) {
    sourceType = 'client';
    sourceLabel = 'Photo Espace Client';
    sourceIcon = '👤';
    sourceColor = 'text-blue-400';
  } else if (hasEmoji) {
    sourceType = 'emoji';
    sourceLabel = 'Emoji';
    sourceIcon = '😊';
    sourceColor = 'text-yellow-400';
  }
  
  return (
    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{sourceIcon}</span>
          <div>
            <p className={`text-sm font-medium ${sourceColor}`}>{sourceLabel}</p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Photo affichée sur ton profil
            </p>
          </div>
        </div>
        
        {/* Indicateurs des sources disponibles */}
        <div className="flex gap-1">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
            hasHeartbeatsPhoto ? 'bg-pink-500 text-white' : 'bg-gray-700 text-gray-500'
          }`} title="Photo Heartbeats">📸</div>
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
            hasAvatarAI ? 'bg-purple-500 text-white' : 'bg-gray-700 text-gray-500'
          }`} title="Avatar IA">✨</div>
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
            hasClientPhoto ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-500'
          }`} title="Photo Client">👤</div>
        </div>
      </div>
      
      {sourceType === 'none' && (
        <p className={`text-xs mt-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
          💡 Ajoute une photo pour avoir plus de matchs !
        </p>
      )}
    </div>
  );
};

// 👤 VUE PRINCIPALE "MON PROFIL"
export const MyProfileView = ({ 
  profile, 
  customer,
  shadowProfile,
  stats,
  onEdit, 
  onEditAvatar,
  onEditFrame,
  onEditPassions,
  onEditVideo, // 🎬 Callback pour modifier la vidéo de présentation
  onOpenShadow,
  onOpenVerification, // 🆕 Callback pour ouvrir le modal de vérification
  onEditPets, // 🐾 Callback pour modifier les animaux
  isDarkMode = true,
  crowns = 0
}) => {
  const [activeSection, setActiveSection] = useState('preview');
  const [showPetsModal, setShowPetsModal] = useState(false);

  if (!profile) {
    return (
      <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <User className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>Aucun profil trouvé</p>
        <Button onClick={onEdit} className="mt-4">
          Créer mon profil
        </Button>
      </div>
    );
  }

  const completionPercentage = calculateProfileCompletion(profile);
  const missingItems = getMissingProfileItems(profile);

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          👤 Mon Profil
        </h2>
        <div className="flex items-center gap-2">
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            👑 {crowns}
          </span>
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Edit className="w-4 h-4 mr-1" />
            Modifier
          </Button>
        </div>
      </div>

      {/* Jauge de complétion */}
      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Profil complété
          </span>
          <span className={`text-sm font-bold ${
            completionPercentage >= 100 ? 'text-green-400' :
            completionPercentage >= 80 ? 'text-green-400' : 
            completionPercentage >= 50 ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {completionPercentage}%
            {completionPercentage >= 100 && ' 🎆'}
          </span>
        </div>
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${completionPercentage}%` }}
            className={`h-full rounded-full ${
              completionPercentage >= 100 ? 'bg-gradient-to-r from-green-400 via-emerald-500 to-cyan-400' :
              completionPercentage >= 80 ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 
              completionPercentage >= 50 ? 'bg-gradient-to-r from-yellow-400 to-orange-500' : 
              'bg-gradient-to-r from-red-400 to-pink-500'
            }`}
          />
        </div>
        
        {/* 🎯 CE QUI MANQUE - Liste cliquable */}
        {completionPercentage < 100 && missingItems.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              🎯 Pour atteindre 100% :
            </p>
            <div className="flex flex-wrap gap-2">
              {missingItems.slice(0, 3).map((item) => (
                <button
                  key={item.id}
                  onClick={onEdit}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105 ${
                    isDarkMode 
                      ? 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30' 
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                  }`}
                >
                  {item.label} <span className="opacity-60">(+{item.points}pts)</span>
                </button>
              ))}
              {missingItems.length > 3 && (
                <span className={`px-3 py-1.5 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  +{missingItems.length - 3} de plus
                </span>
              )}
            </div>
          </div>
        )}
        
        {completionPercentage >= 100 && (
          <p className={`text-xs mt-2 text-green-400 font-medium`}>
            🎆 Félicitations ! Ton profil est complet à 100% !
          </p>
        )}
      </div>

      {/* Aperçu du profil */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50'}`}>
        <p className={`text-xs text-center mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          👀 C&apos;est comme ça que les autres te voient
        </p>
        <ProfilePreviewCard 
          profile={{...profile, dominantSin: shadowProfile?.dominantSin}}
          customer={customer}
          isDarkMode={isDarkMode}
          showEditButton={true}
          onEdit={onEdit}
          size="normal"
        />
      </div>

      {/* Stats */}
      <ProfileStats profile={profile} stats={stats} isDarkMode={isDarkMode} />

      {/* 📸 Source de la photo actuelle */}
      <PhotoSourceIndicator 
        profile={profile} 
        customer={customer} 
        isDarkMode={isDarkMode} 
      />

      {/* Actions rapides */}
      <div className="grid grid-cols-2 gap-3">
        <QuickActionButton
          icon={Camera}
          label="Photos"
          description="Ajoute des photos"
          onClick={onEdit}
          isDarkMode={isDarkMode}
          color="from-pink-500 to-rose-500"
        />
        <QuickActionButton
          icon={Sparkles}
          label="Avatar Magique"
          description="Crée ton avatar"
          onClick={onEditAvatar}
          isDarkMode={isDarkMode}
          color="from-purple-500 to-violet-500"
        />
        <QuickActionButton
          icon={Palette}
          label="Cadre"
          description="Personnalise ton look"
          onClick={onEditFrame}
          isDarkMode={isDarkMode}
          color="from-blue-500 to-cyan-500"
        />
        <QuickActionButton
          icon={Heart}
          label="Passions"
          description="Tes vibrations"
          onClick={onEditPassions}
          isDarkMode={isDarkMode}
          color="from-red-500 to-orange-500"
        />
      </div>

      {/* 🎬 Section Vidéo de Présentation - OPTIONNELLE */}
      <div className={`p-4 rounded-xl border-2 border-dashed ${
        isDarkMode 
          ? 'bg-purple-900/20 border-purple-500/30' 
          : 'bg-purple-50 border-purple-300'
      }`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'
          }`}>
            <Video className="w-6 h-6 text-purple-500" />
          </div>
          <div className="flex-1">
            <h4 className={`font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Vidéo de Présentation
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                isDarkMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
              }`}>
                BONUS
              </span>
            </h4>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Augmente tes chances de match x3 ! (max 60s)
            </p>
          </div>
        </div>
        
        {profile.presentationVideo ? (
          // Vidéo existante
          <div className="text-center">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
              isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'
            }`}>
              <Check className="w-4 h-4" />
              Vidéo ajoutée
            </div>
            {onEditVideo && (
              <Button 
                onClick={onEditVideo}
                size="sm"
                variant="outline"
                className="ml-2 text-xs"
              >
                Modifier
              </Button>
            )}
          </div>
        ) : (
          // Pas de vidéo
          <div className="text-center">
            <p className={`text-xs mb-3 ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
              Les profils avec vidéo reçoivent <strong>3x plus de matchs</strong> ! 💜
            </p>
            {onEditVideo && (
              <Button 
                onClick={onEditVideo}
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                <Video className="w-4 h-4 mr-2" />
                Ajouter ma vidéo
              </Button>
            )}
          </div>
        )}
      </div>

      {/* 🏅 Section Badges de confiance */}
      <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-400" />
            <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Badges de confiance
            </h4>
          </div>
          {profile.badges && profile.badges.length > 0 && (
            <VerificationBadges badges={profile.badges} size="md" />
          )}
        </div>
        
        {/* Badges actuels ou invitation à en obtenir */}
        {(!profile.badges || profile.badges.length === 0) ? (
          <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Les badges inspirent confiance et augmentent tes chances de match ! 💜
          </p>
        ) : (
          <div className="flex flex-wrap gap-2 mb-3">
            {profile.badges.map((badgeId) => {
              const badge = VERIFICATION_BADGES[badgeId];
              if (!badge) return null;
              return (
                <span 
                  key={badgeId}
                  className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${
                    isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {badge.emoji} {badge.name}
                </span>
              );
            })}
          </div>
        )}
        
        {/* Bouton pour obtenir plus de badges */}
        {onOpenVerification && (!profile.badges || !profile.badges.includes('verified') || !profile.badges.includes('email')) && (
          <Button
            onClick={onOpenVerification}
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
            size="sm"
          >
            <Shield className="w-4 h-4 mr-2" />
            {profile.badges && profile.badges.length > 0 ? 'Obtenir plus de badges ✨' : 'Obtenir mes badges ✨'}
          </Button>
        )}
        
        {/* Message si tous les badges sont obtenus */}
        {profile.badges && profile.badges.includes('verified') && profile.badges.includes('email') && (
          <div className={`text-center p-2 rounded-lg ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
              🎉 Tu as tous les badges disponibles !
            </p>
          </div>
        )}
      </div>

      {/* Shadow Mode teaser */}
      {!shadowProfile?.quizCompleted && (
        <motion.div
          whileHover={{ scale: 1.02 }}
          onClick={onOpenShadow}
          className="p-4 rounded-xl bg-gradient-to-r from-purple-900/50 to-black border border-purple-500/30 cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <span className="text-3xl">🌙</span>
            <div className="flex-1">
              <h4 className="font-bold text-white">Shadow Mode</h4>
              <p className="text-sm text-gray-400">Découvre ton péché dominant...</p>
            </div>
            <ChevronRight className="w-5 h-5 text-purple-400" />
          </div>
        </motion.div>
      )}
      
      {/* 🐾 Section Animaux de compagnie */}
      <PetsSection 
        pets={profile.pets || []}
        onEditPets={onEditPets}
        isDarkMode={isDarkMode}
      />
      
      {/* 🌙 Section Nyx 2.0 (si quiz complété) */}
      {shadowProfile?.nyx2Completed && shadowProfile?.nyx2Profile && (
        <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-indigo-950/40 border border-indigo-800' : 'bg-indigo-50 border border-indigo-200'}`}>
          <div className="flex items-center gap-2 mb-3">
            <Moon className="w-5 h-5 text-indigo-400" />
            <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Profil d&apos;Ombre Nyx 2.0
            </h4>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Attachement</p>
              <p className={`font-medium ${isDarkMode ? 'text-indigo-200' : 'text-indigo-800'}`}>
                {shadowProfile.nyx2Profile.attachmentStyle === 'secure' && '🔒 Sécure'}
                {shadowProfile.nyx2Profile.attachmentStyle === 'anxious' && '💭 Anxieux'}
                {shadowProfile.nyx2Profile.attachmentStyle === 'avoidant' && '🏃 Évitant'}
                {shadowProfile.nyx2Profile.attachmentStyle === 'disorganized' && '🌀 Désorganisé'}
              </p>
            </div>
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'}`}>
              <p className={`text-xs ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Blessure</p>
              <p className={`font-medium ${isDarkMode ? 'text-indigo-200' : 'text-indigo-800'}`}>
                {shadowProfile.nyx2Profile.dominantWound === 'rejection' && '💔 Rejet'}
                {shadowProfile.nyx2Profile.dominantWound === 'abandonment' && '🚪 Abandon'}
                {shadowProfile.nyx2Profile.dominantWound === 'humiliation' && '😔 Humiliation'}
                {shadowProfile.nyx2Profile.dominantWound === 'betrayal' && '🗡️ Trahison'}
                {shadowProfile.nyx2Profile.dominantWound === 'injustice' && '⚖️ Injustice'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 🐾 Section Animaux de compagnie
const PetsSection = ({ pets = [], onEditPets, isDarkMode }) => {
  const speciesEmojis = {
    dog: '🐕',
    cat: '🐱',
    bird: '🐦',
    rabbit: '🐰',
    hamster: '🐹',
    fish: '🐠',
    reptile: '🦎',
    other: '🐾'
  };

  return (
    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-amber-950/30 border border-amber-800/50' : 'bg-amber-50 border border-amber-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <PawPrint className={`w-5 h-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
          <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Mes compagnons
          </h4>
        </div>
        {onEditPets && (
          <Button
            onClick={onEditPets}
            variant="ghost"
            size="sm"
            className={isDarkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-700'}
          >
            <Plus className="w-4 h-4 mr-1" />
            {pets.length > 0 ? 'Modifier' : 'Ajouter'}
          </Button>
        )}
      </div>
      
      {pets.length === 0 ? (
        <div className={`text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span className="text-3xl mb-2 block">🐾</span>
          <p className="text-sm">Tu as un compagnon à 4 pattes ?</p>
          <p className="text-xs mt-1">Ajoute-le pour matcher avec d&apos;autres amoureux des animaux !</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pets.map((pet, idx) => (
            <div
              key={idx}
              className={`flex items-center gap-3 p-3 rounded-lg ${
                isDarkMode ? 'bg-amber-900/30' : 'bg-amber-100'
              }`}
            >
              {pet.photo ? (
                <img 
                  src={pet.photo} 
                  alt={pet.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-amber-500"
                />
              ) : (
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${
                  isDarkMode ? 'bg-amber-800' : 'bg-amber-200'
                }`}>
                  {speciesEmojis[pet.species] || '🐾'}
                </div>
              )}
              <div className="flex-1">
                <p className={`font-medium ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>
                  {pet.name}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                  {pet.breed || (
                    pet.species === 'dog' ? 'Chien' :
                    pet.species === 'cat' ? 'Chat' :
                    pet.species === 'bird' ? 'Oiseau' :
                    pet.species === 'rabbit' ? 'Lapin' :
                    pet.species === 'hamster' ? 'Hamster' :
                    pet.species === 'fish' ? 'Poisson' :
                    pet.species === 'reptile' ? 'Reptile' : 'Autre'
                  )}
                  {pet.age && ` • ${pet.age}`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// 🔘 Bouton d'action rapide
const QuickActionButton = ({ icon: Icon, label, description, onClick, isDarkMode, color }) => (
  <motion.button
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={`p-3 rounded-xl text-left ${
      isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
    } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
  >
    <div className={`w-8 h-8 rounded-lg bg-gradient-to-r ${color} flex items-center justify-center mb-2`}>
      <Icon className="w-4 h-4 text-white" />
    </div>
    <h4 className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
      {label}
    </h4>
    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
      {description}
    </p>
  </motion.button>
);

// 📊 Calculer le pourcentage de complétion du profil
export const calculateProfileCompletion = (profile) => {
  if (!profile) return 0;
  
  let score = 0;
  let maxScore = 0;
  
  // Bio (20 points)
  maxScore += 20;
  if (profile.bio && profile.bio.length > 10) score += 20;
  else if (profile.bio) score += 10;
  
  // Photos (25 points)
  maxScore += 25;
  const photoCount = profile.photos?.length || 0;
  score += Math.min(photoCount * 5, 25);
  
  // Avatar (15 points)
  maxScore += 15;
  if (profile.avatarImage) score += 15;
  else if (profile.avatar && profile.avatar !== '😊') score += 5;
  
  // Ville (10 points)
  maxScore += 10;
  if (profile.city) score += 10;
  
  // Passions (20 points)
  maxScore += 20;
  const passionCount = profile.passions ? Object.values(profile.passions).flat().length : 0;
  score += Math.min(passionCount * 2, 20);
  
  // Cadre personnalisé (5 points)
  maxScore += 5;
  if (profile.profileFrame && profile.profileFrame !== 'gradient_purple') score += 5;
  
  // Questions d'âme (5 points)
  maxScore += 5;
  if (profile.soulAnswers && Object.keys(profile.soulAnswers).length >= 3) score += 5;
  
  return Math.round((score / maxScore) * 100);
};

/**
 * 📋 Retourne la liste des éléments manquants pour compléter le profil à 100%
 */
export const getMissingProfileItems = (profile) => {
  if (!profile) return [];
  
  const missing = [];
  
  // Bio
  if (!profile.bio || profile.bio.length <= 10) {
    missing.push({
      id: 'bio',
      label: '✍️ Phrase magique',
      description: 'Ajoute une bio de plus de 10 caractères',
      points: 20,
      section: 'bio'
    });
  }
  
  // Photos
  const photoCount = profile.photos?.length || 0;
  if (photoCount < 5) {
    missing.push({
      id: 'photos',
      label: '📸 Photos',
      description: `Ajoute ${5 - photoCount} photo(s) de plus`,
      points: (5 - photoCount) * 5,
      section: 'photos'
    });
  }
  
  // Avatar
  if (!profile.avatarImage) {
    missing.push({
      id: 'avatar',
      label: '✨ Avatar magique',
      description: 'Crée ton avatar IA stylé',
      points: 15,
      section: 'avatar'
    });
  }
  
  // Ville
  if (!profile.city) {
    missing.push({
      id: 'city',
      label: '📍 Ville',
      description: 'Indique ta ville',
      points: 10,
      section: 'city'
    });
  }
  
  // Passions
  const passionCount = profile.passions ? Object.values(profile.passions).flat().length : 0;
  if (passionCount < 10) {
    missing.push({
      id: 'passions',
      label: '💫 Passions',
      description: `Ajoute ${10 - passionCount} passion(s) de plus`,
      points: Math.min((10 - passionCount) * 2, 20),
      section: 'passions'
    });
  }
  
  // Cadre personnalisé
  if (!profile.profileFrame || profile.profileFrame === 'gradient_purple') {
    missing.push({
      id: 'frame',
      label: '🎨 Cadre unique',
      description: 'Personnalise ton cadre',
      points: 5,
      section: 'frame'
    });
  }
  
  // Questions d'âme
  const soulAnswersCount = profile.soulAnswers ? Object.keys(profile.soulAnswers).length : 0;
  if (soulAnswersCount < 3) {
    missing.push({
      id: 'soul',
      label: '🌙 Questions d\'âme',
      description: `Réponds à ${3 - soulAnswersCount} question(s) de plus`,
      points: 5,
      section: 'soul'
    });
  }
  
  return missing;
};

export default MyProfileView;
